import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { RiLineFill } from "react-icons/ri";
import { Text, XStack, YStack } from "tamagui";

import { colors } from "@styles/colors";
import { resolveThreeDsReturnPath } from "@utils/payThreeDsReturn";

/** 付款完成後幾秒自動回到瓦斯行官方帳號 */
export const OA_REDIRECT_SECONDS = 5;

/** LINE 的品牌綠（與加好友卡一致，一眼認得是回 LINE） */
const LINE_GREEN = "#06C755";

/**
 * 頁面上「可操作的東西」：使用者碰到任何一個就停止倒數（2026-10-28 使用者要求），
 * 之後只能手動按「回到官方帳號」。用 document 層 capture 監聽，付款頁不用逐一接 callback；
 * 自己做的按鈕／開關要帶 role（button／switch）才算得到。
 */
const INTERACTIVE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "label",
  '[role="button"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="link"]',
].join(",");

const STOP_EVENTS = ["pointerdown", "touchstart", "mousedown", "click", "keydown"] as const;

const goTo = (url: string) => {
  // 與加好友卡（<a href>）同一種開法：在目前視窗打開，LINE 內會直接切到官方帳號聊天室
  window.location.href = url;
};

/**
 * 這次開頁是不是「剛付完」：付款成功畫面，或從「付款確認中」輪詢到已付款（銀行慢回的那種）。
 * 傳 threeDsOrderId 時，信用卡 3D 驗證回來（付款前記下的單號就是這張）直接載到已付款也算。
 * 只是之後打開一張早就付過的單（view＝paid）不算，不倒數也不提議加入家庭。
 */
export function useJustPaid(view: string, threeDsOrderId?: string): boolean {
  const [backFromThreeDs] = useState(() => !!threeDsOrderId && resolveThreeDsReturnPath("") === `/pay/${threeDsOrderId}`);
  const prevView = useRef(view);
  const paidAfterProcessing = useRef(false);
  if (prevView.current !== view) {
    if (prevView.current === "processing" && view === "paid") paidAfterProcessing.current = true;
    prevView.current = view;
  }
  return view === "success" || (view === "paid" && (paidAfterProcessing.current || backFromThreeDs));
}

interface OaRedirectCountdownProps {
  /** 官方帳號聊天室網址；沒有（舊後端或瓦斯行沒設定）就整塊不顯示、不倒數 */
  url?: string | null;
  supplierName?: string | null;
  seconds?: number;
  /** 倒數到 0 時怎麼跳轉；預設在目前視窗打開（測試可換掉） */
  onRedirect?: (url: string) => void;
}

/**
 * 付款完成頁：倒數 5 秒自動回到瓦斯行官方帳號＋「回到官方帳號」按鈕（2026-10-28）。
 * 使用者碰了頁面上任何可操作的東西（加入家庭、加好友、註冊等）就停止倒數，只留按鈕。
 */
export const OaRedirectCountdown = ({ url, supplierName, seconds = OA_REDIRECT_SECONDS, onRedirect = goTo }: OaRedirectCountdownProps) => {
  const [left, setLeft] = useState(seconds);
  const [counting, setCounting] = useState(true);
  const active = !!url && counting;
  const redirectRef = useRef(onRedirect);
  redirectRef.current = onRedirect;

  useEffect(() => {
    if (!active) return undefined;
    const id = window.setInterval(() => setLeft((s) => s - 1), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  useEffect(() => {
    if (!active || left > 0 || !url) return;
    setCounting(false);
    redirectRef.current(url);
  }, [active, left, url]);

  // 用 layout effect 在畫面一出現就掛上監聽：passive effect 可能晚一拍，最早的一下觸碰會漏掉
  useLayoutEffect(() => {
    if (!active) return undefined;
    const stop = (e: Event) => {
      const t = e.target;
      if (t instanceof Element && t.closest(INTERACTIVE_SELECTOR)) setCounting(false);
    };
    STOP_EVENTS.forEach((ev) => document.addEventListener(ev, stop, true));
    return () => STOP_EVENTS.forEach((ev) => document.removeEventListener(ev, stop, true));
  }, [active]);

  if (!url) return null;
  const name = supplierName ? `「${supplierName}」` : "";
  return (
    <YStack width="100%" ai="center" gap="$2">
      {active ? (
        <Text fontSize={12.5} color={colors.sub} ta="center" role="timer" aria-live="polite">
          {Math.max(left, 0)} 秒後回到{name}官方帳號
        </Text>
      ) : null}
      <a href={url} style={{ width: "100%", textDecoration: "none" }}>
        <XStack
          br={12}
          py="$3"
          ai="center"
          jc="center"
          gap="$2"
          bg={colors.light}
          bw={1.5}
          bc={colors.border}
          cursor="pointer"
          pressStyle={{ opacity: 0.85 }}
        >
          <RiLineFill size={18} color={LINE_GREEN} />
          <Text fontSize={14} fontWeight="800" color={colors.text}>
            回到官方帳號
          </Text>
        </XStack>
      </a>
    </YStack>
  );
};

export default OaRedirectCountdown;
